/**
 * 词汇搜索（旧 views/dynamic-lexemes/SearchView.vue）：按关键词、状态、语言分页搜索，默认显示待审核
 */
import { useEffect, useState } from 'react'
import { App, Button, Card, Form, Input, Select, Space, Table, Tag, type TableColumnsType } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import { formatDateTime } from '@/utils/datetime'
import { DynamicLexemeStatus, type DynamicLexemeSearchItem, type SearchParams } from '@/types/dynamic-lexemes'
import { lexemeStatusColor, lexemeStatusText } from './constants'
import { useLexemeSearch } from './hooks'
import styles from './DynamicLexemes.module.css'

interface SearchFormValues {
  query?: string
  status?: DynamicLexemeStatus
  language?: string
}

const STATUS_OPTIONS = [
  DynamicLexemeStatus.PENDING_REVIEW,
  DynamicLexemeStatus.APPROVED,
  DynamicLexemeStatus.REJECTED
].map((value) => ({ value, label: lexemeStatusText(value) }))

const LANGUAGE_OPTIONS = [
  { value: 'en', label: '英语' },
  { value: 'zh', label: '中文' }
]

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_FORM: SearchFormValues = { status: DynamicLexemeStatus.PENDING_REVIEW }

// 后端 /search 的状态参数叫 status_filter（旧版发的 status 不生效）
function toSearchParams(values: SearchFormValues, page: number, pageSize: number): SearchParams {
  return {
    query: values.query?.trim() || undefined,
    status_filter: values.status,
    language: values.language,
    page,
    page_size: pageSize
  }
}

const columns: TableColumnsType<DynamicLexemeSearchItem> = [
  { title: '词汇', key: 'word', width: 120, render: (_, record) => <Tag color="blue">{record.word}</Tag> },
  { title: '语言', dataIndex: 'language', width: 80 },
  {
    title: '状态',
    key: 'status',
    width: 100,
    render: (_, record) => <Tag color={lexemeStatusColor(record.status)}>{lexemeStatusText(record.status)}</Tag>
  },
  { title: '遇到次数', dataIndex: 'encounter_count', width: 100 },
  { title: '用户数', dataIndex: 'user_count', width: 80 },
  {
    title: '创建时间',
    key: 'created_at',
    width: 150,
    render: (_, record) => (record.created_at ? formatDateTime(record.created_at) : '--')
  }
]

export function SearchPage() {
  const { message } = App.useApp()
  const [form] = Form.useForm<SearchFormValues>()
  // 进页面默认搜索待审核；为 null 表示已重置、不显示结果
  const [params, setParams] = useState<SearchParams | null>(() => toSearchParams(DEFAULT_FORM, 1, DEFAULT_PAGE_SIZE))
  const { data, isFetching, isError } = useLexemeSearch(params)

  useEffect(() => {
    if (isError) message.error('搜索失败')
  }, [isError, message])

  const search = (values: SearchFormValues) =>
    setParams(toSearchParams(values, 1, params?.page_size ?? DEFAULT_PAGE_SIZE))

  const reset = () => {
    form.setFieldsValue({ query: undefined, status: undefined, language: undefined })
    setParams(null)
  }

  return (
    <div>
      <PageHeader title="词汇搜索" subtitle="搜索和管理所有动态词汇" />

      <Card title="搜索条件" className={styles.block}>
        <Form<SearchFormValues> form={form} layout="inline" initialValues={DEFAULT_FORM} onFinish={search}>
          <Form.Item label="关键词" name="query">
            <Input aria-label="关键词" placeholder="输入词汇或上下文" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="状态" name="status">
            <Select aria-label="状态" placeholder="选择状态" style={{ width: 120 }} allowClear options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item label="语言" name="language">
            <Select aria-label="语言" placeholder="选择语言" style={{ width: 100 }} allowClear options={LANGUAGE_OPTIONS} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />} loading={isFetching}>
                搜索
              </Button>
              <Button onClick={reset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card title="搜索结果">
        <Table<DynamicLexemeSearchItem>
          rowKey="lexeme_id"
          columns={columns}
          dataSource={params ? data?.items : []}
          loading={isFetching}
          pagination={{
            current: params?.page ?? 1,
            pageSize: params?.page_size ?? DEFAULT_PAGE_SIZE,
            total: params ? (data?.total ?? 0) : 0,
            showSizeChanger: true,
            showQuickJumper: true
          }}
          onChange={(pagination) => {
            const values = form.getFieldsValue()
            setParams(toSearchParams(values, pagination.current ?? 1, pagination.pageSize ?? DEFAULT_PAGE_SIZE))
          }}
        />
      </Card>
    </div>
  )
}
